import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, apiGet, apiPost, apiPut } from '../lib/api.ts'
import { useSelectedCampaign } from '../lib/campaign.tsx'

/**
 * Walks: give a walk list to a group of canvassers (migration 0079).
 *
 * Canvassers walk in pairs for safety (groups of 3–4 shelved by the owner,
 * 2026-09-30 — migration 0091), or alone. A pair splits by side of the street: one takes even house numbers,
 * the other odd. Each member's phone leads with their side and can still see
 * the rest. Doors with no house number go on a shared list either can take.
 *
 * Every rule (one person or a pair, canvassers on this campaign, one open walk each, a
 * finished walk list, a live published survey) is enforced by the database;
 * the screen offers only what will pass, and shows the database's sentence
 * when something does not.
 */

type Side = 'even' | 'odd' | 'all'

interface ListExport {
  export_id: string
  name: string | null
  status: string
  row_count: string | null
  list_type_code: string
  universe_name: string
  version_no: number
  completed_at: string | null
  rows_expire_at: string | null
}

interface Turf {
  turf_id: string
  name: string
}

interface Survey {
  survey_id: string
  title: string
  latest_published: number | null
  latest_published_version_id: string | null
}

interface Canvasser {
  user_id: string
  username: string
  display_name: string | null
  has_password: boolean
}

interface Member {
  user_id: string
  username: string
  display_name: string | null
  side: Side
}

interface Assignment {
  assignment_id: string
  name: string
  export_id: string
  export_no: number
  turf_id: string | null
  turf_name: string | null
  survey_version_id: string | null
  survey_title: string | null
  doors: number
  voters: number
  created_at: string
  members: Member[]
}

type Row = { user_id: string; side: Side }

const SIDE_LABEL: Record<Side, string> = { even: 'Even numbers', odd: 'Odd numbers', all: 'Both sides' }

const input = 'rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink'
const label = 'mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase'
const primary =
  'rounded-md bg-brand-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50'
const secondary =
  'rounded-md border border-line px-2.5 py-1.5 text-xs font-semibold hover:border-brand-400 disabled:opacity-50'

/** A pair splits even/odd by default; one person walks both sides. */
function defaultSides(rows: Row[]): Row[] {
  if (rows.length === 1) return [{ ...rows[0]!, side: 'all' }]
  if (rows.length === 2 && rows.every((r) => r.side === 'all')) {
    return [
      { ...rows[0]!, side: 'even' },
      { ...rows[1]!, side: 'odd' },
    ]
  }
  return rows
}

function sideWarning(rows: Row[]): string | null {
  const picked = rows.filter((r) => r.user_id)
  if (picked.length < 2) return null
  const sides = new Set(picked.map((r) => r.side))
  if (!sides.has('all') && !(sides.has('even') && sides.has('odd'))) {
    return `Nobody is walking the ${sides.has('even') ? 'odd' : 'even'} side.`
  }
  return null
}

function listLabel(e: ListExport): string {
  const when = e.completed_at ? new Date(e.completed_at).toLocaleDateString() : ''
  return `${e.name ?? e.universe_name} (v${e.version_no}) — ${Number(e.row_count ?? 0).toLocaleString()} voters · ${when}`
}

function message(caught: unknown): string {
  if (caught instanceof ApiError && caught.status === 403) return 'You are not allowed to do that on this campaign.'
  return caught instanceof Error ? caught.message : 'That did not work.'
}

export function Walks() {
  const client = useQueryClient()
  const { campaignId, campaign } = useSelectedCampaign()
  const allowed = campaign?.can_manage_canvassers === true
  const on = Boolean(campaignId) && allowed

  const exports = useQuery({
    queryKey: ['exports', campaignId],
    queryFn: () => apiGet<{ exports: ListExport[] }>(`/exports?campaign_id=${campaignId}`),
    enabled: on,
  })
  const turf = useQuery({
    queryKey: ['turf', campaignId],
    queryFn: () => apiGet<{ turf: Turf[] }>(`/turf?campaign_id=${campaignId}`),
    enabled: on,
  })
  const surveys = useQuery({
    queryKey: ['surveys', campaignId],
    queryFn: () => apiGet<{ surveys: Survey[] }>(`/surveys?campaign_id=${campaignId}`),
    enabled: on,
  })
  const canvassers = useQuery({
    queryKey: ['canvassers', campaignId],
    queryFn: () => apiGet<{ canvassers: Canvasser[] }>(`/campaigns/${campaignId}/canvassers`),
    enabled: on,
  })
  const assignments = useQuery({
    queryKey: ['walk-assignments', campaignId],
    queryFn: () => apiGet<{ assignments: Assignment[] }>(`/campaigns/${campaignId}/walk-assignments`),
    enabled: on,
  })

  const walkLists = (exports.data?.exports ?? []).filter(
    (e) => e.list_type_code === 'walk' && e.status === 'complete',
  )
  const publishedSurveys = (surveys.data?.surveys ?? []).filter((s) => s.latest_published_version_id)
  const open = assignments.data?.assignments ?? []
  // Who is already out on a walk: the database allows one open walk each.
  const busyIn = (except?: string) => {
    const m = new Map<string, string>()
    for (const a of open) {
      if (a.assignment_id === except) continue
      for (const x of a.members) m.set(x.user_id, a.name)
    }
    return m
  }
  const busy = busyIn()

  const [error, setError] = useState<string | null>(null)
  const [exportId, setExportId] = useState('')
  const [turfId, setTurfId] = useState('')
  const [surveyVersionId, setSurveyVersionId] = useState('')
  const [name, setName] = useState('')
  const [rows, setRows] = useState<Row[]>([{ user_id: '', side: 'all' }])

  const people = canvassers.data?.canvassers ?? []

  function reset() {
    setExportId('')
    setTurfId('')
    setSurveyVersionId('')
    setName('')
    setRows([{ user_id: '', side: 'all' }])
  }

  // Switching campaign in the header clears a half-built assignment: its list,
  // turf and canvassers belong to the campaign it was started on.
  useEffect(() => {
    setError(null)
    reset()
  }, [campaignId])

  const refresh = () => client.invalidateQueries({ queryKey: ['walk-assignments', campaignId] })

  const assign = useMutation({
    mutationFn: () =>
      apiPost(`/campaigns/${campaignId}/walk-assignments`, {
        export_id: exportId,
        turf_id: turfId || null,
        survey_version_id: surveyVersionId || null,
        name: name.trim(),
        members: rows.filter((r) => r.user_id),
      }),
    onSuccess: async () => {
      setError(null)
      reset()
      await refresh()
    },
    onError: (e) => setError(message(e)),
  })

  const close = useMutation({
    mutationFn: (a: Assignment) => apiPost(`/campaigns/${campaignId}/walk-assignments/${a.assignment_id}/close`),
    onSuccess: async () => {
      setError(null)
      await refresh()
    },
    onError: (e) => setError(message(e)),
  })

  const [editing, setEditing] = useState<string | null>(null)
  const [editRows, setEditRows] = useState<Row[]>([])
  const saveMembers = useMutation({
    mutationFn: (a: Assignment) =>
      apiPut(`/campaigns/${campaignId}/walk-assignments/${a.assignment_id}/members`, {
        members: editRows.filter((r) => r.user_id),
      }),
    onSuccess: async () => {
      setError(null)
      setEditing(null)
      await refresh()
    },
    onError: (e) => setError(message(e)),
  })

  const picked = rows.filter((r) => r.user_id)
  const warning = sideWarning(rows)
  const canAssign = exportId && name.trim() && picked.length >= 1 && !assign.isPending

  return (
    <div>
      <h1 className="text-xl font-semibold tracking-tight">Walks</h1>
      <p className="mt-1 text-sm text-ink-muted">
        Give a walk list to one canvasser or a pair. A pair splits the street: one takes the even
        numbers, the other the odd.
      </p>


      {campaign && !allowed ? (
        <p className="mt-6 text-sm text-ink-muted">Only the Campaign Manager can assign walks on this campaign.</p>
      ) : null}

      {error ? (
        <p role="alert" className="mt-4 rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm text-ink">
          {error}
        </p>
      ) : null}

      {allowed ? (
        <>
          <section className="mt-6 rounded-lg border border-line bg-surface-sunken p-4">
            <h2 className="text-sm font-semibold">Assign a walk</h2>

            {walkLists.length === 0 && !exports.isPending ? (
              <p className="mt-2 text-sm">
                There is no finished walk list yet. Cut one on{' '}
                <Link to="/turf" className="underline">Map areas</Link> or{' '}
                <Link to="/lists" className="underline">Lists</Link> first.
              </p>
            ) : null}
            {people.length === 0 && !canvassers.isPending ? (
              <p className="mt-2 text-sm">
                There are no canvassers yet. Add them on{' '}
                <Link to="/canvassers" className="underline">Canvassers</Link> first.
              </p>
            ) : null}

            <form
              className="mt-3 space-y-4"
              onSubmit={(e) => {
                e.preventDefault()
                if (canAssign) assign.mutate()
              }}
            >
              <div className="flex flex-wrap items-end gap-3">
                <label className="w-full text-sm sm:w-auto">
                  <span className={label}>Walk list</span>
                  <select
                    value={exportId}
                    onChange={(e) => {
                      setExportId(e.target.value)
                      const l = walkLists.find((w) => w.export_id === e.target.value)
                      if (l && !name) setName(l.name ?? l.universe_name)
                    }}
                    className={`${input} w-full sm:w-96`}
                  >
                    <option value="">Choose a walk list</option>
                    {walkLists.map((e) => (
                      <option key={e.export_id} value={e.export_id}>{listLabel(e)}</option>
                    ))}
                  </select>
                </label>
                <label className="w-full text-sm sm:w-auto">
                  <span className={label}>Turf (optional)</span>
                  <select
                    value={turfId}
                    onChange={(e) => {
                      setTurfId(e.target.value)
                      const t = (turf.data?.turf ?? []).find((x) => x.turf_id === e.target.value)
                      if (t) setName(t.name)
                    }}
                    className={`${input} w-full sm:w-56`}
                  >
                    <option value="">None</option>
                    {(turf.data?.turf ?? []).map((t) => (
                      <option key={t.turf_id} value={t.turf_id}>{t.name}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="flex flex-wrap items-end gap-3">
                <label className="w-full text-sm sm:w-auto">
                  <span className={label}>Name</span>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={120}
                    placeholder="e.g. Saturday — Precinct 19000001"
                    className={`${input} w-full sm:w-72`}
                  />
                </label>
                <label className="w-full text-sm sm:w-auto">
                  <span className={label}>Survey (optional)</span>
                  <select
                    value={surveyVersionId}
                    onChange={(e) => setSurveyVersionId(e.target.value)}
                    className={`${input} w-full sm:w-64`}
                  >
                    <option value="">No survey — outcomes only</option>
                    {publishedSurveys.map((s) => (
                      <option key={s.survey_id} value={s.latest_published_version_id!}>
                        {s.title} (v{s.latest_published})
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <fieldset>
                <legend className={label}>Who is walking</legend>
                <GroupRows
                  rows={rows}
                  setRows={setRows}
                  people={people}
                  busy={busy}
                  inputClass={input}
                />
                {warning ? <p className="mt-2 text-sm font-semibold">{warning}</p> : null}
              </fieldset>

              <button type="submit" className={primary} disabled={!canAssign}>
                {assign.isPending ? 'Assigning…' : 'Assign walk'}
              </button>
            </form>
          </section>

          <h2 className="mt-8 text-sm font-semibold tracking-wide text-ink-muted uppercase">Out walking</h2>
          {assignments.isPending ? (
            <p className="mt-2 text-sm text-ink-muted">Loading…</p>
          ) : open.length === 0 ? (
            <p className="mt-2 text-sm text-ink-muted">No open walks.</p>
          ) : (
            <ul className="mt-2 divide-y divide-line rounded-lg border border-line">
              {open.map((a) => (
                <li key={a.assignment_id} className="p-4" data-testid="walk">
                  <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
                    <div className="min-w-0 flex-1 basis-56">
                      <div className="font-medium">{a.name}</div>
                      <div className="text-xs text-ink-muted">
                        {a.doors.toLocaleString()} doors · {a.voters.toLocaleString()} voters · list #{a.export_no}
                        {a.turf_name ? ` · ${a.turf_name}` : ''}
                        {a.survey_title ? ` · survey: ${a.survey_title}` : ' · no survey'}
                      </div>
                    </div>
                    <ul className="min-w-0 flex-1 basis-56 space-y-0.5 text-sm">
                      {a.members.map((m) => (
                        <li key={m.user_id}>
                          {m.display_name ?? m.username}{' '}
                          <span className="text-xs text-ink-muted">— {SIDE_LABEL[m.side]}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className={secondary}
                        onClick={() => {
                          setEditing(a.assignment_id)
                          setEditRows(a.members.map((m) => ({ user_id: m.user_id, side: m.side })))
                        }}
                      >
                        Change group
                      </button>
                      <button
                        type="button"
                        className={secondary}
                        disabled={close.isPending}
                        onClick={() => {
                          if (confirm(`Close “${a.name}”? The phones on it lose the walk at their next check-in.`)) {
                            close.mutate(a)
                          }
                        }}
                      >
                        Close walk
                      </button>
                    </div>
                  </div>

                  {editing === a.assignment_id ? (
                    <div className="mt-3 rounded-md border border-line bg-surface-sunken p-3" role="group" aria-label="Change group">
                      <GroupRows
                        rows={editRows}
                        setRows={setEditRows}
                        people={people}
                        busy={busyIn(a.assignment_id)}
                        inputClass={input}
                      />
                      {sideWarning(editRows) ? <p className="mt-2 text-sm font-semibold">{sideWarning(editRows)}</p> : null}
                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          className={primary}
                          disabled={saveMembers.isPending || editRows.filter((r) => r.user_id).length === 0}
                          onClick={() => saveMembers.mutate(a)}
                        >
                          Save group
                        </button>
                        <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => setEditing(null)}>
                          Cancel
                        </button>
                      </div>
                      <p className="mt-2 text-xs text-ink-muted">
                        Anyone taken off loses the walk at their next check-in.
                      </p>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}
    </div>
  )
}

/** One or two rows of (canvasser, side) — pairs only for now (0091). */
function GroupRows({
  rows,
  setRows,
  people,
  busy,
  inputClass,
}: {
  rows: Row[]
  setRows: (rows: Row[]) => void
  people: Canvasser[]
  busy: Map<string, string>
  inputClass: string
}) {
  const chosen = new Set(rows.map((r) => r.user_id).filter(Boolean))
  const update = (i: number, patch: Partial<Row>) =>
    setRows(defaultSides(rows.map((r, j) => (j === i ? { ...r, ...patch } : r))))

  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2" data-testid="group-row">
          <select
            aria-label={`Canvasser ${i + 1}`}
            value={r.user_id}
            onChange={(e) => update(i, { user_id: e.target.value })}
            className={`${inputClass} w-full sm:w-64`}
          >
            <option value="">Choose a canvasser</option>
            {people.map((p) => {
              const onWalk = busy.get(p.user_id)
              const taken = chosen.has(p.user_id) && p.user_id !== r.user_id
              return (
                <option key={p.user_id} value={p.user_id} disabled={Boolean(onWalk) || taken || !p.has_password}>
                  {p.display_name ?? p.username}
                  {onWalk ? ` — on “${onWalk}”` : !p.has_password ? ' — needs a new password' : ''}
                </option>
              )
            })}
          </select>
          <select
            aria-label={`Side for canvasser ${i + 1}`}
            value={r.side}
            onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, side: e.target.value as Side } : x)))}
            className={`${inputClass} w-full sm:w-40`}
          >
            <option value="even">{SIDE_LABEL.even}</option>
            <option value="odd">{SIDE_LABEL.odd}</option>
            <option value="all">{SIDE_LABEL.all}</option>
          </select>
          {rows.length > 1 ? (
            <button
              type="button"
              className="px-2 text-xs text-ink-muted hover:text-ink"
              onClick={() => setRows(defaultSides(rows.filter((_, j) => j !== i)))}
            >
              Remove
            </button>
          ) : null}
        </div>
      ))}
      {rows.length < 2 ? (
        <button
          type="button"
          className={secondary}
          onClick={() => setRows(defaultSides([...rows, { user_id: '', side: 'all' }]))}
        >
          + Add someone
        </button>
      ) : (
        <p className="text-xs text-ink-muted">A walk is one person or a pair.</p>
      )}
    </div>
  )
}
